import { isEmpty, kebabCase } from 'lodash';
import { FC, Fragment, ReactElement } from 'react';
import { useToggle } from 'react-use';

import { BaseBlock } from '@/components/base-block';
import { Icon } from '@/components/icon';
import { Link } from '@/components/link';
import type { Width } from '@/lib/types';

import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { ProductFeature } from './features';

const ANALYTICS_CONTEXT = 'CTA - Table';

type FeatureValue = {
  type: string;
  isIncluded: boolean;
  conditions?: string;
};

export type Product = {
  title: string;
  icon?: string;
  label?: string;
  description?: string;
  price?: {
    monthly?: string;
    annual?: string;
    single?: string;
    hasAnnualVehicleSuffix?: boolean;
  };
  url?: string;
  isFeatured: boolean;
  featuredLabel?: string;
  features: Array<FeatureValue>;
};

type RoadsideFeatureTableProps = {
  product: Product;
  features: Array<ProductFeature>;
  limit?: number;
  terms?: ReactElement;
  width?: Width;
  anchor?: string | null;
};

const TooltipTrigger: FC<{ feature: ProductFeature }> = ({ feature }) => {
  const hasDescription = Boolean(feature.description);

  if (!hasDescription) {
    return null;
  }

  return (
    <OverlayTrigger
      rootClose
      trigger="click"
      placement="top"
      overlay={
        <Popover className="popover" id={feature.title}>
          <Popover.Title>{feature.title}</Popover.Title>
          <Popover.Content>
            {feature.description}
            {feature.link && feature.link.url && <Link href={feature.link.url}>{feature.link.label}</Link>}
          </Popover.Content>
        </Popover>
      }
    >
      <button type="button" className="rft-more-info-trigger" aria-label="What does this mean?">
        <Icon name="question-circle" />
      </button>
    </OverlayTrigger>
  );
};

const Feature: FC<{ feature: ProductFeature; product: Product }> = ({ feature, product }) => {
  const productFeature = product.features.find(({ type }) => type === feature.type);
  return (
    <Fragment key={kebabCase(feature.title)}>
      <tr className="rft-label-row" aria-hidden>
        <th colSpan={2}>
          <span>{feature.title}</span>
          <TooltipTrigger feature={feature} />
        </th>
      </tr>

      <tr>
        <th className="rft-label-cell">
          <span>{feature.title}</span>
          <TooltipTrigger feature={feature} />
        </th>

        <td key={product.title}>
          <Icon
            name={productFeature?.isIncluded ? 'check' : 'times'}
            colour={productFeature?.isIncluded ? 'Green' : null}
          />
          <span className="sr-only">{productFeature?.isIncluded ? 'Yes' : 'No'}</span>
          {productFeature?.conditions && <span className="rft-conditions">{productFeature?.conditions}</span>}
        </td>
      </tr>
    </Fragment>
  );
};

const RoadsideFeatureTable: FC<RoadsideFeatureTableProps> = ({
  features,
  product,
  limit = 14,
  terms = null,
  width = 'Narrow',
  anchor = null,
}) => {
  const [showAllFeatures, toggleShowAllFeatures] = useToggle(false);
  const initialFeatures = features.slice(0, limit);
  const otherFeatures = features.slice(limit);
  const hasInitialFeatures = !isEmpty(initialFeatures);
  const hasOtherFeatures = !isEmpty(otherFeatures);
  const hasProductPricing = Boolean(product.price);
  const hasProductLinks = Boolean(product.url);
  const hasProductDescription = Boolean(product.description);
  const hasMonthlyPrice = Boolean(product?.price?.monthly);
  const hasAnnualPrice = Boolean(product?.price?.annual);
  const hasSinglePrice = Boolean(product?.price?.single);
  return (
    <BaseBlock className="comparison-block" width={width} anchor={anchor} hasContentPadding={false}>
      <table className="roadside-feature-table">
        <thead>
          <tr className="roadside-product-titles">
            <th colSpan={2} key={product.title} className="p-0 p-sm-4">
              {product.icon && <Icon className="fa-3x text-reset" name={product.icon} />}
              <span className="thlink">
                <span className="roadside-product-title h1 text-reset">{product.title}</span>
              </span>
            </th>
          </tr>

          {hasProductPricing && (
            <tr className="roadside-product-prices">
              <td colSpan={2} key={product.title}>
                {hasSinglePrice && <span className="roadside-product-price h6">${product?.price?.single}</span>}
                {hasMonthlyPrice && (
                  <>
                    <span className="roadside-product-price h1">${product?.price?.monthly}</span>
                    <span className="roadside-product-price-units moabbr h6">/month</span>
                  </>
                )}
                {hasAnnualPrice && (
                  <>
                    <span className="roadside-product-price-units h6">
                      <br />
                      or ${product?.price?.annual}
                    </span>
                    <span className="roadside-product-price-units h6">/yr</span>
                  </>
                )}
                {product?.price?.hasAnnualVehicleSuffix && (
                  <>
                    <span className="roadside-product-price-units moabbr h6">/yr</span>
                    <span className="roadside-product-price-units h6">
                      <br />
                      per vehicle
                    </span>
                  </>
                )}
              </td>
            </tr>
          )}

          {hasProductDescription && (
            <tr className="roadside-product-descriptions d-none d-md-table-row">
              <td className="rft-label-cell" />

              <td key={product.title}>
                <span className="roadside-product-description body-5">{product.description}</span>
              </td>
            </tr>
          )}
        </thead>

        <tbody>
          {hasInitialFeatures &&
            initialFeatures.map((feature) => <Feature key={feature.type} feature={feature} product={product} />)}

          {hasOtherFeatures && (
            <>
              {showAllFeatures ? (
                otherFeatures.map((feature) => <Feature key={feature.type} feature={feature} product={product} />)
              ) : (
                <tr className="roadside-feature-table-teaser">
                  <td className="filler-row" aria-hidden colSpan={2} />
                  <td className="filler-cell" />
                  <td className="filler-cell" />
                </tr>
              )}
              <tr className="roadside-feature-table-show-extra">
                <td colSpan={2}>
                  <button
                    type="button"
                    className="btn btn-sm btn-link roadside-feature-table-toggle-all-features"
                    onClick={() => toggleShowAllFeatures()}
                  >
                    <Icon className="mr-2" name={showAllFeatures ? 'minus-circle' : 'plus-circle'} />
                    {showAllFeatures ? 'Show fewer details' : ' Show all details'}
                  </button>
                </td>
              </tr>
            </>
          )}
        </tbody>

        {hasProductLinks && (
          <tfoot>
            <tr>
              {product.url && (
                <td colSpan={2} key={product.title}>
                  <Link
                    href={product.url}
                    className="btn btn-sm btn-primary"
                    target="_blank"
                    analytics={{
                      context: ANALYTICS_CONTEXT,
                      text: product.title,
                    }}
                  >
                    <span>Get {product.title}</span>
                  </Link>
                </td>
              )}
            </tr>
          </tfoot>
        )}
      </table>

      {terms && <div className="mt-3 roadside-feature-table-terms">{terms}</div>}
    </BaseBlock>
  );
};

export { RoadsideFeatureTable };
